import * as React from 'react';
import { getInertProps } from './side-nav-inert';
import { cn } from '@utils';

export interface ISideNavFrameProps {
	collapsed: boolean;
	isMobile: boolean;
	header?: React.ReactNode;
	collapsedHeader?: React.ReactNode;
	controls?: React.ReactNode;
	footer?: React.ReactNode;
	children: React.ReactNode;
	itemsAriaLabel: string;
	itemsAsList: boolean;
}

export const SideNavFrame: React.FC<ISideNavFrameProps> = ({
	collapsed,
	isMobile,
	header,
	collapsedHeader,
	controls,
	footer,
	children,
	itemsAriaLabel,
	itemsAsList,
}) => {
	const showExpandedChrome = !collapsed;
	const itemsPaddingClass = isMobile ? 'p-[1.2rem]' : 'p-[1rem]';

	return (
		<>
			{collapsed && collapsedHeader ? (
				<div
					className="mb-[0.5rem] flex flex-col items-center gap-0 border-b border-white/20 px-[0.5rem] py-[2rem]"
					data-testid="side-nav-collapsed-header"
				>
					{collapsedHeader}
				</div>
			) : null}

			<div
				className={cn(
					'shrink-0 overflow-hidden transition-opacity duration-[250ms]',
					showExpandedChrome ? 'relative visible opacity-100' : 'pointer-events-none absolute h-0 opacity-0'
				)}
				aria-hidden={collapsed || undefined}
				{...getInertProps(collapsed)}
				data-testid="side-nav-header"
			>
				{header}
				{controls}
			</div>

			<div className="menu-items-wrapper min-h-0 flex-1 overflow-y-auto overflow-x-hidden" data-testid="side-nav-items">
				{itemsAsList ? (
					// eslint-disable-next-line jsx-a11y/no-redundant-roles -- list-none strips implicit list semantics in Safari
					<ul
						role="list"
						aria-label={itemsAriaLabel}
						className={cn('m-0 list-none', itemsPaddingClass)}
						data-testid="side-nav-menu-list"
					>
						{children}
					</ul>
				) : (
					<div className={itemsPaddingClass} data-testid="side-nav-items-status">
						{children}
					</div>
				)}
			</div>

			<div
				className={cn(
					'menu-bottom-wrapper shrink-0 border-t border-white/10 p-[1rem] transition-opacity duration-[250ms]',
					collapsed
						? 'pointer-events-none absolute h-0 overflow-hidden opacity-0'
						: 'relative visible opacity-100'
				)}
				aria-hidden={collapsed || undefined}
				{...getInertProps(collapsed)}
				data-testid="side-nav-footer"
			>
				{footer}
			</div>
		</>
	);
};

SideNavFrame.displayName = 'SideNavFrame';
